import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import ArrowBack from '@mui/icons-material/ArrowBack'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import useAuthStore from '../store/authStore'
import { authService } from '../services/authService'
import LoadingAnimation from '../components/common/LoadingAnimation'
import { applyPreferredLanguageFromProfile } from '../utils/applyProfileLanguage'

const FAVICON = '/favicon.png'
const LOGO_FALLBACK = '/assets/images/ui_elements/logo.png'
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const LoginPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { setAuth, updateTokens } = useAuthStore()
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [identifierError, setIdentifierError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const successMessage = location.state?.successMessage

  const validateIdentifier = (value) => {
    const v = String(value || '').trim()
    if (!v) return ''
    if (v.includes('@')) {
      if (!EMAIL_PATTERN.test(v)) return t('auth.invalidEmailFormat')
      return ''
    }
    if (v.length < 3) return t('auth.usernameTooShort', 'Username must be at least 3 characters.')
    return ''
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    const trimmed = identifier.trim()
    if (!trimmed) {
      setIdentifierError(t('auth.emailOrUsernameRequired', 'Enter your email or username.'))
      return
    }
    const idErr = validateIdentifier(identifier)
    setIdentifierError(idErr)
    if (idErr) return
    setLoading(true)

    try {
      const { access_token, refresh_token } = await authService.login(trimmed, password)
      updateTokens(access_token, refresh_token)
      const profile = await authService.getCurrentUser()
      await applyPreferredLanguageFromProfile(profile)
      setAuth(profile, access_token, refresh_token)
      navigate('/dashboard')
    } catch (err) {
      setError(err.response?.data?.detail || t('auth.loginFailed'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-gradient-to-b from-green-50 to-white py-8 md:py-12 px-4 sm:px-6 lg:px-8">
      <Link
        to="/"
        className="absolute start-4 top-6 flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 sm:start-6 md:start-8"
        aria-label={t('common.back')}
      >
        <ArrowBack sx={{ fontSize: 20 }} className="rtl:scale-x-[-1]" />
        <span className="hidden sm:inline">{t('common.back')}</span>
      </Link>
      <div className="max-w-md w-full space-y-6 md:space-y-8 bg-white rounded-lg shadow-lg p-6 md:p-8">
        <div className="text-center">
          <Link to="/" className="inline-block focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-full">
            <img
              src={FAVICON}
              alt={t('common.appName')}
              className="w-20 h-20 md:w-28 md:h-28 mx-auto mb-2 object-contain rounded-full"
              onError={(e) => {
                e.target.src = LOGO_FALLBACK
                e.target.onerror = null
                e.target.classList.remove('rounded-full')
              }}
            />
          </Link>
          <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">
            {t('common.login')}
          </h2>
        </div>
        <form className="space-y-4 md:space-y-6" noValidate onSubmit={handleSubmit}>
          {successMessage && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-3 md:px-4 py-2 md:py-3 rounded flex items-center gap-2">
              <span className="text-green-600">✓</span>
              <span className="text-sm md:text-base font-medium">{successMessage}</span>
            </div>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 md:px-4 py-2 md:py-3 rounded flex items-center gap-2">
              <img
                src="/assets/images/ui_elements/icon_warning.png"
                alt="Error"
                className="w-5 h-5 flex-shrink-0"
                onError={(e) => {
                  e.target.src = '/assets/images/ui_elements/icon_error.png'
                }}
              />
              <span className="text-sm md:text-base">{error}</span>
            </div>
          )}
          <div className="space-y-3 md:space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('auth.emailOrUsername', 'Email or username')}
              </label>
              <input
                type="text"
                required
                value={identifier}
                onChange={(e) => {
                  const next = e.target.value
                  setIdentifier(next)
                  setIdentifierError(validateIdentifier(next))
                }}
                onBlur={() => setIdentifierError(validateIdentifier(identifier))}
                autoComplete="username"
                className={`appearance-none relative block w-full px-3 md:px-4 py-2 md:py-2.5 border placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-2 text-sm md:text-base ${
                  identifierError
                    ? 'border-red-400 focus:border-red-500 focus:ring-red-200'
                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                }`}
                placeholder={t('auth.placeholderEmailOrUsername', 'you@example.com or your username')}
              />
              {identifierError && (
                <p className="mt-1.5 text-xs font-medium text-red-600">{identifierError}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('auth.password')}</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none relative block w-full py-2 md:py-2.5 pl-12 md:pl-14 pr-3 md:pr-4 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm md:text-base"
                  placeholder={t('auth.placeholderPassword')}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  className="absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                >
                  {showPassword ? <VisibilityOff sx={{ fontSize: 22 }} /> : <Visibility sx={{ fontSize: 22 }} />}
                </button>
              </div>
            </div>
          </div>
          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center items-center py-2.5 md:py-3 border border-transparent text-sm md:text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? <LoadingAnimation size="sm" /> : t('common.login')}
            </button>
          </div>
          <div className="text-center">
            <Link to="/register" className="text-sm md:text-base text-primary-600 hover:text-primary-500 font-medium">
              {t('common.register')}
            </Link>
          </div>
        </form>
      </div>
    </div>
  )
}

export default LoginPage
